<template>
  <!--
    手语数字人 web 插件封装(Vue 2) + 自写菜单卡片
    - 关闭插件自带悬浮球(showFloatButton:false),面板本体保留,用 call("toggleWidget") 控制显隐
    - 两种挂载模式:
      * 默认(非 inline):position:absolute 跟随父容器,卡片在右下角。
        父容器需 position:relative 且不滚动。articleDetails 用此模式。
      * inline:卡片作为普通流元素融入父布局,用于嵌入 TextReading 的
        actions-wrap 竖排操作栏。5 个 TextReading 页面用此模式。
    - 卡片图片 src/assets/img/menu_shuzhiren.png(345x71 菜单卡片)
    - 点击卡片弹出速度卡片(click 触发);面板展开/收起走卡片内按钮,与弹卡片解耦
    - 划词自动翻译不依赖悬浮球,关掉自带球后仍可用
  -->
  <div v-if="isSignLanguage" :class="inline ? 'sl-dh-inline' : 'sl-dh-layer'">
    <el-popover
      placement="left-start"
      trigger="click"
      v-model="cardVisible"
      popper-class="sl-speed-card"
    >
      <!-- 卡片内容:按 UI 设计 -->
      <div class="sl-card">
        <div class="sl-card__header">
          <div class="sl-card__main-title">手语数字人设置</div>
        </div>
        <div class="sl-card__content">
          <div class="sl-card__title">播放速度</div>
          <div class="sl-card__speeds">
            <button
              v-for="opt in speedOptions"
              :key="opt.rate"
              type="button"
              class="sl-speed-btn"
              :class="{ 'is-active': currentSpeed === opt.rate }"
              @click="onSpeed(opt.rate)"
            >
              {{ opt.label }}
            </button>
          </div>
          <div class="sl-card__divider"></div>
          <div class="sl-card__actions">
            <button class="sl-btn sl-btn--help" @click="onHelp">
              使用帮助
            </button>
            <button class="sl-btn sl-btn--stop" @click="onStop">
              停止手语
            </button>
          </div>
        </div>
      </div>

      <!-- 菜单卡片图片(reference):click 触发弹卡片,不切面板 -->
      <div
        slot="reference"
        :class="[
          inline ? 'sl-inline-card' : 'sl-float-card',
          { 'is-expanded': isExpanded },
        ]"
      >
        <img
          class="sl-card-img"
          :src="require('@/assets/img/menu_shuzhiren.png')"
          alt="手语数字人"
        />
      </div>
    </el-popover>

    <!-- 帮助弹层(内容自填) -->
    <el-dialog
      title="手语翻译使用帮助"
      :visible.sync="helpVisible"
      :modal="false"
      width="620px"
      custom-class="sl-help-dialog"
      append-to-body
    >
      <div class="sl-help-content" v-html="helpContent"></div>
    </el-dialog>

    <!-- 划词字数提示气泡:固定定位跟随选区,pointer-events:none 不挡划词 -->
    <div
      v-show="selectionTooltip.visible"
      class="sl-selection-tooltip"
      :class="[
        `is-${selectionTooltip.placement}`,
        { 'is-over': selectionTooltip.count > maxChars },
      ]"
      :style="tooltipStyle"
    >
      已选 {{ selectionTooltip.count }} / {{ maxChars }} 字
      <span
        v-if="selectionTooltip.count > maxChars"
        class="sl-selection-tooltip__hint"
        >超出部分不翻译</span
      >
    </div>
  </div>
</template>

<script>
/**
 * 手语数字人 web 插件封装(Vue 2)
 *
 * 真实接口(见 public/signlanguageDigitalHumanV1.js,从压缩源码确认):
 *   window.slDigitalHumanWidget.init(cfg) -> { instance, destroy() }
 *   window.slDigitalHumanWidget.call(method, ...args)
 *
 * call 可用方法(expose 列表):
 *   playSignLanguage(text)  翻译并播放;text="" 等同停止手语
 *   refreshDigitalHuman()   刷新数字人(重载 iframe)
 *   toggleWidget()          展开/收起面板(无独立 show/hide)
 *   setPlaybackSpeed(rate)  设速度,rate ∈ 0.5/1/1.5/2
 *   enableToolbar/disableToolbar/showToolbar/hideToolbar  工具栏(本次未用)
 *   demo / currentMode / chatRef / digitalPeopleRef       内部,勿用
 *
 * 关键点:
 *   1. init() 会在 document.body 上插入 #xd-chat-widget-container,组件销毁时
 *      必须调用返回句柄的 destroy() 清理,否则重复挂载。
 *   2. 插件脚本(约 1.2MB 预构建 bundle)放在 public/ 下,运行时按需注入
 *      <script> 加载,只有进入接入页面才会拉取。
 *   3. 自写悬浮球为 position:absolute,需放在 position:relative 且不滚动的
 *      父容器内,球出现在该容器右下角。
 *
 * initialPosition {x,y}:展开面板相对 viewport 的像素坐标,x=left、y=top,
 *   面板内部会 clamp 到视口内(四边留 16px)。默认 null,组件自动计算为
 *   「贴视口右侧、垂直居中」;传入则尊重传入值。
 *
 * 尺寸 props(init 后固定,运行时不可改):
 *   panelWidth/panelHeight    外层面板宽高,默认 400px/640px
 *   digitalWidth/digitalHeight 数字人 iframe 宽高,默认同面板(数字人填满)
 *   开启 welComeText 时,组件自动让 digitalHeight = panelHeight - 20px 留提示条空间。
 *   responsive=true 时,上述尺寸被覆盖:面板高 = clamp(innerHeight×viewportScale,
 *   viewportMin, viewportMax),宽按 400:640 等比跟随。仅在 init 时算一次,
 *   不随 resize 变化(重载数字人需 10-20 秒)。
 *
 * welComeText/ifWelComeText:欢迎提示。开启后模型加载完成显示提示条、手语播放
 *   该文案,约 15 秒后提示条自动消失(插件一次性欢迎语,非常驻)。
 */

// 运管平台下发的前端插件凭证(前端写死)
const SIGN_LANGUAGE_CREDENTIAL = {
  appSecret: 'rDksqd7bfXgzYZwW',
  tenantId: 'bjlycx',
  pluginCode: 'bjly-web',
};

const PLUGIN_SCRIPT_ID = 'signlanguage-digital-human-script';

// 速度档位(照搬插件自带 speed-card):rate 为调用 setPlaybackSpeed 的入参
const SPEED_OPTIONS = [
  { label: '0.5x', rate: 0.5 },
  { label: '1.0x', rate: 1 },
  { label: '1.5x', rate: 1.5 },
  { label: '2.0x', rate: 2 },
];

// 数字人默认宽高比(panelWidth:panelHeight = 400:640)。
// responsive 模式下宽度按此比例跟随高度,保证等比缩放不变形。
const DIGITAL_ASPECT_RATIO = 400 / 640;

// 划词翻译字数上限(与帮助文档一致:100 个汉字/标点)。
// 仅用于前端气泡提示,不干预插件实际翻译。
const SIGN_LANGUAGE_MAX_CHARS = 100;

/**
 * 节流:拖动划词时 selectionchange 高频触发,节流后气泡平滑跟随又不卡顿。
 * 带 trailing:保证松手时最后一次选区变化一定回显。cancel() 供组件销毁清理定时器。
 */
function throttle(fn, wait) {
  let last = 0;
  let timer = null;
  const throttled = function (...args) {
    const now = Date.now();
    const remaining = wait - (now - last);
    if (remaining <= 0) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      last = now;
      fn.apply(this, args);
    } else if (!timer) {
      timer = setTimeout(() => {
        last = Date.now();
        timer = null;
        fn.apply(this, args);
      }, remaining);
    }
  };
  throttled.cancel = function () {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return throttled;
}

/**
 * 按需加载插件脚本:IIFE 执行后会在 window 上挂载 slDigitalHumanWidget。
 * 已加载则直接 resolve,避免重复注入。
 */
function loadPluginScript() {
  if (window.slDigitalHumanWidget) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(PLUGIN_SCRIPT_ID);
    if (existing) {
      // 脚本标签已存在但尚未执行完毕,等待其 load/error 事件
      if (window.slDigitalHumanWidget) return resolve();
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('加载手语数字人插件失败')),
      );
      return;
    }

    const script = document.createElement('script');
    script.id = PLUGIN_SCRIPT_ID;
    script.src = `${process.env.BASE_URL}signlanguageDigitalHumanV1.js`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('加载手语数字人插件失败'));
    document.body.appendChild(script);
  });
}

export default {
  name: 'SignLanguageDigitalHuman',
  props: {
    // 展开面板初始位置 {x, y},相对 viewport 像素坐标,面板会自动 clamp 到视口内。
    // 默认 null:由组件自动计算为「贴视口右侧、垂直居中」。传入则尊重传入值。
    initialPosition: {
      type: Object,
      default: null,
    },
    // 默认是否展开面板
    defaultExpanded: {
      type: Boolean,
      default: true,
    },
    // 是否以内联模式渲染:球作为普通流元素(40px)融入父布局,
    // 如 TextReading 的 actions-wrap 竖排操作栏。默认 false(右下角悬浮大球)。
    inline: {
      type: Boolean,
      default: false,
    },
    // 外层面板宽(字符串带单位)。默认 400px。
    panelWidth: {
      type: String,
      default: '400px',
    },
    // 外层面板高(字符串带单位)。默认 640px。
    panelHeight: {
      type: String,
      default: '640px',
    },
    // 数字人 iframe 宽(字符串带单位)。默认同 panelWidth,数字人填满面板。
    digitalWidth: {
      type: String,
      default: '400px',
    },
    // 数字人 iframe 高(字符串带单位)。默认同 panelHeight,数字人填满面板。
    // 开启 welComeText 时,组件会自动留 20px 给提示条(见 resolveDigitalHeight)。
    digitalHeight: {
      type: String,
      default: '640px',
    },
    // 是否按视口自适应面板尺寸(覆盖 panelWidth/Height、digitalWidth/Height)。
    // 开启后 init 时按视口高度算一次 px 并 clamp 上下限,不随窗口 resize 变化
    // (插件 init 一次性,重载数字人需 10-20 秒,实时响应不现实)。
    // 宽度按数字人宽高比(400:640)等比例跟随高度,避免拉伸变形。
    // 默认 true:组件默认即自适应;传 :responsive="false" 回退固定尺寸。
    responsive: {
      type: Boolean,
      default: true,
    },
    // responsive 模式下面板高度 = clamp(window.innerHeight × viewportScale, viewportMin, viewportMax)。
    viewportScale: {
      type: Number,
      default: 0.55,
    },
    // responsive 模式下面板高度下限(px)。
    viewportMin: {
      type: Number,
      default: 400,
    },
    // responsive 模式下面板高度上限(px)。
    viewportMax: {
      type: Number,
      default: 600,
    },
    // 欢迎提示文案。开启后,数字人模型加载完成会显示提示条、并用手语播放该文案,
    // 约 15 秒后提示条自动消失(插件一次性欢迎语机制,非常驻)。
    welComeText: {
      type: String,
      default: '欢迎使用手语划词翻译,请用鼠标选中要翻译的字词',
    },
    // 是否启用欢迎提示条。默认 true。
    ifWelComeText: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      handle: null,
      ready: false, // 插件脚本加载 + init 完成
      isExpanded: this.defaultExpanded, // 面板展开态镜像(仅 toggleWidget 同步)
      currentSpeed: 1, // 当前速度倍率
      speedOptions: SPEED_OPTIONS,
      cardVisible: false, // 速度卡片显隐
      helpVisible: false, // 帮助弹层显隐
      // 帮助文档内容(HTML),按需自行替换
      helpContent:
        '<h4>一、文档概述</h4>' +
        '<p>本文档为手语翻译插件使用说明，涵盖核心手语翻译能力、数字人控制功能全部操作细则，面向普通用户，明确功能边界、使用限制与异常规则。</p>' +
        '<h4>二、核心模块功能说明</h4>' +
        '<h5>2.1 初始化数字人</h5>' +
        '<p>为插件前置基础功能。首次打开插件、数字人画面卡顿/错位、手势渲染异常时，需执行初始化操作。执行后会重置数字人姿态、画面位置、默认播放速度，清空未完成的手语翻译，修复画面渲染故障，初始化过程耗时约10-20秒，期间无法操作其他手语功能。</p>' +
        '<h5>2.2 手语翻译（核心功能）</h5>' +
        '<p><strong>2.2.1 开始手语翻译</strong></p>' +
        '<p>1. 操作逻辑：鼠标划词确定内容后开始翻译，数字人实时通过国家通用手语打出对应文本语义；若当前数字人正在执行手语翻译，重新划词后会强制中断原有任务，直接播放最新划词内容。</p>' +
        '<p>2. 字数限制：输入文本最大支持100个汉字/标点符号，超出字数系统将自动截取前100位字符，超出部分不进行翻译与展示，无弹窗提示。</p>' +
        '<p>3. 兼容范围：仅支持简体中文、常规中英文标点，不支持生僻异体字、特殊符号、emoji表情，非常规字符会被自动过滤。</p>' +
        '<p><strong>2.2.2 停止手语翻译</strong></p>' +
        '<p>主动终止当前正在进行的手语翻译动作，数字人回归默认站立静止姿态，清空后台待翻译手语序列。</p>' +
        '<p><strong>2.2.3 设置手语翻译速度</strong></p>' +
        '<p>独立速度调节，可选择固定播放倍率，默认标准速度为1.0倍。</p>' +
        '<h5>2.3 附属功能控制</h5>' +
        '<p><strong>2.3.1 划词功能</strong></p>' +
        '<p>鼠标拖动选中页面任意纯文本内容，松开鼠标即手语播报选中文字；仅支持网页静态文本，图片内文字、加密文档文字无法识别朗读。</p>' +
        '<p><strong>2.3.2 数字人基础控制</strong></p>' +
        '<p>1. 数字人开关：独立总控开关，关闭后隐藏数字人画面，所有手语动作停止，开启后恢复默认参数展示数字人。</p>' +
        '<p>2. 数字人拖拽移动：点击拖拽控件，可自由拖拽至浏览器界面任意位置，不会被页面元素遮挡。</p>' +
        '<p>3. 数字人大小缩放：仅支持等比例缩放，不支持拉伸变形。缩放后不会影响手语手势动作精度。</p>' +
        '<h4>三、常见问题简易排查</h4>' +
        '<p>1. 数字人手势卡顿：执行【初始化数字人】即可修复。</p>' +
        '<p>2. 文本翻译无动作：检查是否超出100字限制，缩减文本后重试。</p>' +
        '<p>3. 无法拖拽数字人：确认数字人开关处于开启状态，退出服务后重新加载插件。</p>',
      // 划词字数提示气泡(方案 A:仅提示,不干预插件翻译)
      // visible 显隐 / count 已选字数 / x,y 锚点视口坐标 / placement 上方或下方
      selectionTooltip: {
        visible: false,
        count: 0,
        x: 0,
        y: 0,
        placement: 'top',
      },
      maxChars: SIGN_LANGUAGE_MAX_CHARS,
    };
  },
  computed: {
    // 面板高度(带单位):responsive 时按视口算并 clamp;否则用传入的 panelHeight。
    resolvedPanelHeight() {
      if (!this.responsive) return this.panelHeight;
      const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
      const h = Math.round(vh * this.viewportScale);
      return `${Math.min(this.viewportMax, Math.max(this.viewportMin, h))}px`;
    },
    // 面板宽度(带单位):responsive 时按数字人宽高比等比例跟随高度;否则用 panelWidth。
    resolvedPanelWidth() {
      if (!this.responsive) return this.panelWidth;
      const h = parseInt(this.resolvedPanelHeight, 10) || 640;
      return `${Math.round(h * DIGITAL_ASPECT_RATIO)}px`;
    },
    // 数字人 iframe 宽(带单位):responsive 时填满面板宽;否则尊重 digitalWidth prop。
    resolvedDigitalWidth() {
      return this.responsive ? this.resolvedPanelWidth : this.digitalWidth;
    },
    // 气泡内联样式:固定定位到选区锚点,placement 决定往上/往下偏移
    tooltipStyle() {
      const t = this.selectionTooltip;
      return {
        left: `${t.x}px`,
        top: `${t.y}px`,
      };
    },
    isSignLanguage() {
      return this.$route.query.signLang == '1';
    },
  },
  mounted() {
    if (!this.isSignLanguage) return;
    this.initWidget();
    this.bindSelectionListener();
  },
  // 该组件仅在非 keep-alive 的详情页使用,mounted/beforeDestroy 即可成对触发
  beforeDestroy() {
    this.destroyWidget();
    this.unbindSelectionListener();
  },
  methods: {
    /**
     * 计算面板初始位置:贴视口右侧、垂直居中。
     * - x 传超大值,插件内部 clamp 到「视口宽 - 面板宽(350) - 16」-> 贴右留 16px
     * - y 动态算 (视口高 - 面板高420) / 2 -> 垂直居中
     * 若父组件显式传了 initialPosition prop,则尊重传入值。
     */
    resolveInitialPosition() {
      if (this.initialPosition) return this.initialPosition;
      // 面板高度用于垂直居中计算,从 resolvedPanelHeight 解析数字部分
      // (responsive 时是按视口算出的高度,否则为传入的 panelHeight)
      const panelHeightNum = parseInt(this.resolvedPanelHeight, 10) || 640;
      const viewportHeight =
        typeof window !== 'undefined' ? window.innerHeight : 800;
      return {
        x: 99999,
        y: Math.max(16, (viewportHeight - panelHeightNum) / 2),
      };
    },
    /**
     * 计算数字人 iframe 高度:开启 welComeText 时,若未显式留空间,
     * 自动从 panelHeight 减 20px 给底部提示条,避免提示条挤压数字人。
     */
    resolveDigitalHeight() {
      const panelHeightNum = parseInt(this.resolvedPanelHeight, 10) || 640;
      // responsive 时数字人填满面板(忽略 digitalHeight prop);否则尊重传入值
      const digitalHeightNum = this.responsive
        ? panelHeightNum
        : parseInt(this.digitalHeight, 10) || 640;
      if (this.ifWelComeText && digitalHeightNum >= panelHeightNum) {
        return `${panelHeightNum - 20}px`;
      }
      return this.responsive ? `${panelHeightNum}px` : this.digitalHeight;
    },
    async initWidget() {
      try {
        await loadPluginScript();
        const widget = window.slDigitalHumanWidget;
        if (!widget || typeof widget.init !== 'function') {
          console.warn(
            '[手语数字人] 插件未正确挂载到 window.slDigitalHumanWidget',
          );
          return;
        }
        const config = {
          ...SIGN_LANGUAGE_CREDENTIAL,
          defaultExpanded: this.defaultExpanded,
          showFloatButton: false, // 关闭自带悬浮球,用自写球
          initialPosition: this.resolveInitialPosition(),
          width: this.resolvedPanelWidth, // 外层面板宽(responsive 时按视口算)
          height: this.resolvedPanelHeight, // 外层面板高(responsive 时按视口算)
          digitalWidth: this.resolvedDigitalWidth, // 数字人 iframe 宽
          digitalHeight: this.resolveDigitalHeight(), // 数字人 iframe 高(开启提示条时自动留 20px)
          welComeText: this.welComeText, // 欢迎提示文案
          ifWelComeText: this.ifWelComeText, // 是否显示提示条
        };
        this.handle = widget.init(config);
        this.ready = true;
      } catch (err) {
        console.error('[手语数字人] 初始化失败:', err);
      }
    },
    destroyWidget() {
      if (this.handle && typeof this.handle.destroy === 'function') {
        this.handle.destroy();
      }
      this.handle = null;
      this.ready = false;
    },
    /** 展开/收起面板(由卡片内按钮触发,与弹卡片解耦) */
    onTogglePanel() {
      if (!this.ready) {
        if (this.$message) this.$message.info('数字人加载中,请稍候');
        return;
      }
      this.call('toggleWidget');
      this.isExpanded = !this.isExpanded;
    },
    /** 切换播放速度 */
    onSpeed(rate) {
      this.currentSpeed = rate;
      this.call('setPlaybackSpeed', rate);
    },
    /** 停止手语:播放空文本(与插件自带停止按钮实现一致) */
    onStop() {
      this.call('playSignLanguage', '');
    },
    /** 打开帮助 */
    onHelp() {
      this.helpVisible = true;
    },
    /**
     * 透传调用插件能力,供父组件通过 $refs 使用,例如:
     *   this.$refs.signLanguage.call("playSignLanguage", "测试文字")
     *   this.$refs.signLanguage.call("toggleWidget")
     * 未初始化时仅告警,不抛错(与插件 call 行为一致)。
     */
    call(method, ...args) {
      const widget = window.slDigitalHumanWidget;
      if (widget && typeof widget.call === 'function') {
        return widget.call(method, ...args);
      }
      console.warn('[手语数字人] 未初始化,call 被跳过');
      return undefined;
    },
    /**
     * 绑定划词字数提示(方案 A):
     * 监听 document selectionchange(80ms 节流,拖动时平滑跟随、松手 trailing 回显),
     * 取选区文本字数,在选区附近显示「已选 N/100 字」气泡,超 100 字标红提示。
     * 不干预插件翻译,仅做提示。排除项与插件 me() 一致(插件面板/表单/可编辑/忽略标记)。
     */
    bindSelectionListener() {
      this.onSelectionChange = throttle(() => this.handleSelectionChange(), 80);
      document.addEventListener(
        'selectionchange',
        this.onSelectionChange,
      );
      // 滚动时选区 rect 失效,隐藏气泡(捕获模式兼容 bscroll-view 等内部滚动容器)
      this.onScrollHide = () => {
        if (this.selectionTooltip.visible) this.selectionTooltip.visible = false;
      };
      window.addEventListener('scroll', this.onScrollHide, true);
    },
    unbindSelectionListener() {
      if (this.onSelectionChange) {
        document.removeEventListener('selectionchange', this.onSelectionChange);
        this.onSelectionChange.cancel();
        this.onSelectionChange = null;
      }
      if (this.onScrollHide) {
        window.removeEventListener('scroll', this.onScrollHide, true);
        this.onScrollHide = null;
      }
      this.selectionTooltip.visible = false;
    },
    /** selectionchange 节流回调:计算字数与气泡位置 */
    handleSelectionChange() {
      const sel = window.getSelection();
      // 无选区 / 选区折叠(仅光标) / 空文本 -> 隐藏
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        this.selectionTooltip.visible = false;
        return;
      }
      const text = sel.toString();
      if (!text || !text.trim()) {
        this.selectionTooltip.visible = false;
        return;
      }
      const range = sel.getRangeAt(0);
      const node = range.commonAncestorContainer;
      const el = node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
      if (!el || !(el instanceof Element)) {
        this.selectionTooltip.visible = false;
        return;
      }
      // 排除项与插件 me() 一致:在这些区域划词不弹气泡(插件也不会翻译)
      // - #xd-chat-widget-container:数字人面板本体
      // - input/textarea/contenteditable:表单与可编辑区
      // - [data-ignore-selection="true"]:显式忽略标记
      if (
        el.closest('#xd-chat-widget-container') ||
        el.closest(
          'input, textarea, [contenteditable="true"], [contenteditable=""]',
        ) ||
        el.closest('[data-ignore-selection="true"]')
      ) {
        this.selectionTooltip.visible = false;
        return;
      }
      const count = text.trim().length;
      const rect = range.getBoundingClientRect();
      // 气泡预估尺寸,用于判断上方空间是否足够 + 水平 clamp
      const TOOLTIP_H = 32;
      const HALF_W = 70;
      let placement = 'top';
      let y = rect.top;
      // 上方空间不足 -> 翻到选区下方
      if (rect.top - TOOLTIP_H - 8 < 0) {
        placement = 'bottom';
        y = rect.bottom;
      }
      // 水平居中于选区,并 clamp 进视口避免溢出
      const x = Math.min(
        Math.max(rect.left + rect.width / 2, HALF_W),
        window.innerWidth - HALF_W,
      );
      this.selectionTooltip = { visible: true, count, x, y, placement };
    },
  },
};
</script>

<style scoped>
/* 划词字数提示气泡:固定定位贴选区,不拦截鼠标(否则挡划词操作) */
.sl-selection-tooltip {
  position: fixed;
  z-index: 99999;
  transform: translateX(-50%);
  padding: 6px 12px;
  border-radius: 8px;
  background: rgba(51, 51, 51, 0.92);
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  font-family: AlibabaPuHuiTiM, sans-serif;
  line-height: 1.4;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
  transition: opacity 0.12s ease;
}
/* 上方:气泡底边贴选区顶部,留 8px 间距 */
.sl-selection-tooltip.is-top {
  transform: translate(-50%, calc(-100% - 8px));
}
/* 下方:气泡顶边贴选区底部,留 8px 间距 */
.sl-selection-tooltip.is-bottom {
  transform: translate(-50%, 8px);
}
/* 超出字数:红底警示 */
.sl-selection-tooltip.is-over {
  background: rgba(255, 51, 51, 0.92);
}
.sl-selection-tooltip__hint {
  margin-left: 6px;
  opacity: 0.92;
}

/* 定位层:相对于视口定位,不拦截页面点击 */
.sl-dh-layer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  z-index: 999;
}

/* 菜单卡片图片:出现在视口右上角,导航栏下面 */
.sl-float-card {
  position: absolute;
  right: 24px;
  top: calc(60px + 24px);
  pointer-events: auto;
  cursor: pointer;
  transition: transform 0.18s ease;
}
.sl-float-card:hover {
  transform: translateY(-2px);
}

/* ---- inline 模式:卡片作为普通流元素融入父布局(如 actions-wrap 竖排) ---- */
.sl-dh-inline {
  display: flex;
  align-items: center;
  justify-content: center;
}
.sl-inline-card {
  cursor: pointer;
  transition: transform 0.18s ease;
}
.sl-inline-card:hover {
  transform: translateY(-2px);
}

/* 卡片图片:原生 3 倍图(1062x219),显示尺寸 345x71 */
.sl-card-img {
  display: block;
  width: 345px;
  height: auto;
  user-select: none;
  -webkit-user-drag: none;
}
</style>

<!--
  速度卡片 / 帮助弹层由 el-popover / el-dialog 挂到 body,脱离 scoped 作用域,
  需用全局样式 + popper-class / custom-class 定位。
-->
<style>
/*
  覆盖第三方插件数字人面板样式(只改视觉外壳:圆角/背景/边框/阴影,尺寸布局不动)。
  - .main-panel 是外层面板,自带 border-radius:16px + overflow:hidden,
    圆角与阴影加在这里(圆角才真正可见,阴影不被外层裁剪)。
  - .panel-content 是内层内容区,背景/边框加在这里。
  - 用 #xd-chat-widget-container 容器 ID 提升优先级,碾压插件 scoped 样式。
*/
#xd-chat-widget-container .main-panel {
  border-radius: 20px !important;
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.2) !important;
}
#xd-chat-widget-container .panel-content {
  border-radius: 20px !important;
  background: linear-gradient(
    180deg,
    #ffffffe6 0%,
    #f8f9ffe6 44%,
    #f3f3ffe6 100%
  ) !important;
  border: 4px solid #ffffff !important;
}

.sl-speed-card {
  min-width: 280px !important;
  padding: 0 !important;
  z-index: 99999 !important;
  border-radius: 20px !important;
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.15) !important;
  background: #ffffff !important;
}
.sl-speed-card .el-popover__content {
  padding: 0 !important;
}
.sl-speed-card .popper__arrow {
  display: none;
}
.sl-card {
  padding: 24px 20px 20px;
}
.sl-card__header {
  margin-bottom: 16px;
}
.sl-card__main-title {
  font-size: 20px;
  font-weight: 600;
  font-family: AlibabaPuHuiTiB, sans-serif;
  color: #000000;
  line-height: 1.2;
}
.sl-card__content {
  display: flex;
  flex-direction: column;
}
.sl-card__title {
  font-size: 14px;
  font-weight: 500;
  font-family: AlibabaPuHuiTiM, sans-serif;
  color: #909399;
  margin-bottom: 12px;
}
.sl-card__speeds {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.sl-speed-btn {
  height: 40px;
  border: none;
  border-radius: 20px;
  background: #e5e6eb;
  color: #909399;
  font-size: 16px;
  font-weight: 500;
  font-family: AlibabaPuHuiTiM, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sl-speed-btn:hover {
  opacity: 0.85;
}
.sl-speed-btn.is-active {
  background: #333333;
  color: #ffffff;
}
.sl-card__divider {
  height: 1px;
  background: #e5e6eb;
  margin-bottom: 16px;
}
.sl-card__actions {
  display: flex;
  gap: 16px;
}
.sl-btn {
  flex: 1;
  height: 40px;
  border-radius: 20px;
  font-size: 15px;
  font-weight: 500;
  font-family: AlibabaPuHuiTiM, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;
  background: transparent;
}
.sl-btn:hover {
  opacity: 0.85;
}
.sl-btn--help {
  border-color: #333333;
  color: #333333;
}
.sl-btn--stop {
  border-color: #ff3333;
  color: #ff3333;
}
.sl-help-content {
  max-height: 50vh;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.8;
  color: #4e5969;
  /* 内容定高滚动:右侧留滚动条空间,避免文字被遮挡 */
  padding-right: 8px;
  box-sizing: border-box;
}
.sl-help-content p {
  margin: 0 0 8px;
}
.sl-help-content h4 {
  margin: 16px 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2329;
}
.sl-help-content h4:first-child {
  margin-top: 0;
}
.sl-help-content h5 {
  margin: 12px 0 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2329;
}
.sl-help-content strong {
  color: #1f2329;
}
</style>
